<template>
  <div
    class="space-y-3"
    :class="isRefreshing ? 'opacity-90' : 'opacity-100'"
  >
    <div class="grid grid-cols-2 gap-3 sm:gap-4">
      <SessionStatCard
        :title="t('app.views.cashRegister.initialBalance') || 'Saldo Inicial'"
        :value="formatCurrency(initialBalance)"
        :is-refreshing="isRefreshing"
      >
        <template #icon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 text-emerald-500/80 dark:text-emerald-300/70"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z"
              clip-rule="evenodd"
            />
          </svg>
        </template>
      </SessionStatCard>

      <SessionStatCard
        :title="t('app.views.cashRegister.currentBalance') || 'Saldo Actual'"
        :value="formatCurrency(currentBalance)"
        :is-refreshing="isRefreshing"
      >
        <template #icon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 text-sky-500/80 dark:text-sky-300/70"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z"
              clip-rule="evenodd"
            />
          </svg>
        </template>
      </SessionStatCard>
    </div>

    <div class="grid grid-cols-2 gap-3 sm:gap-4">
      <SessionStatCard
        :title="t('app.views.cashRegister.salesAmount') || 'Monto de Ventas'"
        :value="formatCurrency(salesAmount)"
        :is-refreshing="isRefreshing"
      >
        <template #icon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 text-emerald-500/80 dark:text-emerald-300/70"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </template>
      </SessionStatCard>

      <SessionStatCard
        :title="t('app.views.cashRegister.salesCount') || 'Transacciones de Venta'"
        :value="salesCount"
        :is-refreshing="isRefreshing"
      >
        <template #icon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 text-cyan-500/80 dark:text-cyan-300/70"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
          </svg>
        </template>
      </SessionStatCard>
    </div>

    <div class="grid grid-cols-2 gap-3 sm:gap-4">
      <SessionStatCard
        :title="t('app.views.cashRegister.totalExpenses') || 'Gastos Totales'"
        :value="formatCurrency(totalExpenses)"
        :is-refreshing="isRefreshing"
      >
        <template #icon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 text-amber-500/80 dark:text-amber-300/70"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z"
              clip-rule="evenodd"
            />
          </svg>
        </template>
      </SessionStatCard>

      <SessionStatCard
        :title="t('app.views.cashRegister.expensesCount') || 'Transacciones de Gastos'"
        :value="expensesCount"
        :is-refreshing="isRefreshing"
      >
        <template #icon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 text-orange-500/80 dark:text-orange-300/70"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
            <path
              fill-rule="evenodd"
              d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd"
            />
          </svg>
        </template>
      </SessionStatCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { formatCurrency } from '@/utils/priceHelpers';
import SessionStatCard from '@/components/cashRegister/SessionStatCard.vue';

const { t } = useI18n();

defineProps<{
  initialBalance: number;
  currentBalance: number;
  salesAmount: number;
  salesCount: number;
  totalExpenses: number;
  expensesCount: number;
  isRefreshing?: boolean;
}>();
</script>
